import { toAssignmentMap } from './assignmentMap';
import { isKnownExperiment, isKnownVariant } from './experiments';
import { readSnapshot, writeSnapshot, resolveForce, STICKY_WINDOW_MS } from './experimentStorage';
import type { SnapshotAssignments } from './experimentStorage';
import type { ExperimentAssignment, ExperimentResolution } from './types';

interface ResolveContext {
  environment: string;
  searchParams: URLSearchParams;
}

/** Validate snapshot assignments against the registry → Map (unknown experiment/variant dropped). */
function mapFromSnapshot(assignments: SnapshotAssignments): Map<string, string> {
  const map = new Map<string, string>();
  for (const [name, variant] of Object.entries(assignments)) {
    if (isKnownExperiment(name) && isKnownVariant(name, variant)) {
      map.set(name, variant);
    }
  }
  return map;
}

/** experimentName → set of variant codes the backend flags HardStop=true (discontinued). */
function buildStoppedSet(
  raw: readonly ExperimentAssignment[] | undefined
): Map<string, Set<string>> {
  const stopped = new Map<string, Set<string>>();
  for (const a of raw ?? []) {
    if (!a.experimentName) continue;
    for (const v of a.availableVariants ?? []) {
      if (v.hardStop && v.variantCode) {
        const set = stopped.get(a.experimentName) ?? new Set<string>();
        set.add(v.variantCode);
        stopped.set(a.experimentName, set);
      }
    }
  }
  return stopped;
}

/**
 * A HardStopped (killed) variant must never be served, even from a sticky snapshot. Replace it
 * with the backend's current non-stopped assignment (base/alternative) or, when none remains,
 * remove the entry so useExperiment falls back to 'control'. Returns the evicted experiment names.
 */
function evictStopped(
  map: Map<string, string>,
  stopped: Map<string, Set<string>>,
  backendMap: Map<string, string>
): Set<string> {
  const evicted = new Set<string>();
  for (const [name, variant] of [...map]) {
    if (!stopped.get(name)?.has(variant)) continue;
    const replacement = backendMap.get(name);
    if (replacement && !stopped.get(name)?.has(replacement)) {
      map.set(name, replacement);
    } else {
      map.delete(name);
    }
    evicted.add(name);
  }
  return evicted;
}

/**
 * Resolves the effective experiment assignments for this page load.
 *
 * Sticky (anti-flicker): first visit freezes the validated backend set into one
 * browser-global snapshot for 14 days; while non-expired it is the sole source
 * (backend ignored, incl. new experiments). Empty snapshot ({}) keeps the
 * unmodified page for the window.
 *
 * Precedence: ?abForce (non-prod) > HardStop eviction (backend has killed the variant) >
 * snapshot (non-expired) > backend (no snapshot).
 *
 * Non-prod: a known experiment with a registry-unknown backend variant is
 * seed/registry drift and throws loudly; production stays lenient.
 *
 * Returns one resolution per experiment the backend assigned or the page shows.
 */
export function resolveExperimentAssignments(
  rawAssignments: ExperimentAssignment[] | undefined,
  ctx: ResolveContext,
  now: number = Date.now()
): Map<string, ExperimentResolution> {
  const isProd = (ctx.environment ?? '').toLowerCase() === 'production';

  if (!isProd) {
    assertNoRegistryDrift(rawAssignments);
  }

  const backendMap = toAssignmentMap(rawAssignments);
  const stopped = buildStoppedSet(rawAssignments);

  // Sticky snapshot wins for its whole 14-day window; otherwise capture the backend set now.
  const snapshot = readSnapshot(now);
  let map: Map<string, string>;
  let expiresAt: number;
  let persist: boolean;
  if (snapshot) {
    map = mapFromSnapshot(snapshot.assignments);
    expiresAt = snapshot.expiresAt;
    persist = false;
  } else {
    map = new Map(backendMap);
    expiresAt = now + STICKY_WINDOW_MS;
    persist = true;
  }

  // HardStop kill-switch always beats the snapshot: evict any discontinued variant in favour of
  // the backend base/alternative (or 'control'), and persist the correction within the window.
  const evicted = evictStopped(map, stopped, backendMap);
  if (evicted.size > 0) {
    persist = true;
  }
  if (persist) {
    writeSnapshot(Object.fromEntries(map), expiresAt);
  }

  // ?abForce (non-prod) — highest precedence, not persisted.
  const force = resolveForce(ctx.searchParams, ctx.environment);
  let forced: string | undefined;
  if (force && isKnownExperiment(force.name) && isKnownVariant(force.name, force.variant)) {
    map.set(force.name, force.variant);
    forced = force.name;
  }

  return toResolutions(rawAssignments, backendMap, map, {
    base: snapshot ? 'snapshot' : 'backend',
    forced,
    evicted,
  });
}

/** Non-prod: a known experiment with a registry-unknown backend variant is seed/registry drift. */
function assertNoRegistryDrift(rawAssignments: ExperimentAssignment[] | undefined): void {
  for (const a of rawAssignments ?? []) {
    if (
      a.experimentName &&
      isKnownExperiment(a.experimentName) &&
      a.assignedVariantCode &&
      !isKnownVariant(a.experimentName, a.assignedVariantCode)
    ) {
      throw new Error(
        `[experiments] known experiment "${a.experimentName}" returned unknown variant ` +
          `"${a.assignedVariantCode}" — registry/seed drift`
      );
    }
  }
}

/** One resolution per experiment the backend assigned or the page shows. */
function toResolutions(
  rawAssignments: ExperimentAssignment[] | undefined,
  backendMap: Map<string, string>,
  shownMap: Map<string, string>,
  sources: { base: ExperimentResolution['source']; forced?: string; evicted: Set<string> }
): Map<string, ExperimentResolution> {
  const resolutions = new Map<string, ExperimentResolution>();
  for (const name of new Set([...backendMap.keys(), ...shownMap.keys()])) {
    let source = sources.base;
    if (name === sources.forced) source = 'force';
    else if (sources.evicted.has(name)) source = 'hardstop';
    resolutions.set(name, {
      experimentId: backendMap.has(name)
        ? rawAssignments?.find((a) => a.experimentName === name)?.experimentId
        : undefined,
      backendVariant: backendMap.get(name),
      shownVariant: shownMap.get(name),
      source,
    });
  }
  return resolutions;
}
